import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateProfile } from '../utils/api';
import Swal from 'sweetalert2';
import './EditProfile.css';

function EditProfile() {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        nickname: '',
        email: '',
        country: '',
        birthDate: '',
        profilePhoto: null,
        passportPhoto: null
    });
    const [previews, setPreviews] = useState({
        profilePhoto: null,
        passportPhoto: null
    });
    const [currentProfilePhoto, setCurrentProfilePhoto] = useState(null);
    const [currentPassportPhoto, setCurrentPassportPhoto] = useState(null);

    useEffect(() => {
        if (!user?.id && !user?._id) {
            navigate('/');
            return;
        }

        // Load user profile
        const loadProfile = async () => {
            try {
                const userId = user.id || user._id;
                const profile = await getUserProfile(userId);
                
                setFormData({
                    nickname: profile.nickname || '',
                    email: profile.email || '',
                    country: profile.country || '',
                    birthDate: profile.birthDate ? new Date(profile.birthDate).toISOString().split('T')[0] : '',
                    profilePhoto: null,
                    passportPhoto: null
                });

                // Set current photo URLs
                if (profile.profilePhoto) {
                    setCurrentProfilePhoto(profile.profilePhoto);
                }
                if (profile.passportPhoto && user.gender === 'female') {
                    setCurrentPassportPhoto(profile.passportPhoto);
                }
            } catch (error) {
                console.error('Error loading profile:', error);
                Swal.fire('Error', 'Failed to load profile', 'error');
            }
        };

        loadProfile();
    }, [user, navigate]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        const fieldName = e.target.name;

        if (file) {
            setFormData({
                ...formData,
                [fieldName]: file
            });

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviews({
                    ...previews,
                    [fieldName]: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const userId = user.id || user._id;
            const data = new FormData();
            
            data.append('nickname', formData.nickname);
            if (formData.email) {
                data.append('email', formData.email);
            }
            if (formData.country) {
                data.append('country', formData.country);
            }
            if (formData.birthDate) {
                data.append('birthDate', formData.birthDate);
            }
            if (formData.profilePhoto) {
                data.append('profilePhoto', formData.profilePhoto);
            }
            if (formData.passportPhoto && user.gender === 'female') {
                data.append('passportPhoto', formData.passportPhoto);
            }

            const response = await updateProfile(userId, data);
            
            // Update user in context
            updateUser(response.user);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Profile updated successfully',
                timer: 2000,
                showConfirmButton: false
            });

            // Navigate back to dashboard
            if (user.gender === 'male') {
                navigate('/male-dashboard');
            } else {
                navigate('/female-dashboard');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            const errorMessage = error.response?.data?.message || 'Failed to update profile';
            Swal.fire('Error', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const canEditPassport = user.gender === 'female' && (!user.isApproved || user.status !== 'approved');

    return (
        <div className="edit-profile-page">
            <div className="edit-profile-container">
                <div className="edit-profile-card">
                    <h1>Edit Profile</h1>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="nickname">Nickname *</label>
                            <input
                                type="text"
                                id="nickname"
                                name="nickname"
                                value={formData.nickname}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </div>

                        {user.gender === 'female' && (
                            <div className="form-group">
                                <label htmlFor="country">Country *</label>
                                <input
                                    type="text"
                                    id="country"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="birthDate">Birth Date *</label>
                            <input
                                type="date"
                                id="birthDate"
                                name="birthDate"
                                value={formData.birthDate}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="profilePhoto">Profile Photo</label>
                            {currentProfilePhoto && !previews.profilePhoto && (
                                <div className="current-photo">
                                    <img 
                                        src={`/${currentProfilePhoto}`} 
                                        alt="Current profile" 
                                        className="photo-preview"
                                    />
                                    <p className="photo-label">Current photo</p>
                                </div>
                            )}
                            {previews.profilePhoto && (
                                <div className="new-photo">
                                    <img 
                                        src={previews.profilePhoto} 
                                        alt="New profile" 
                                        className="photo-preview"
                                    />
                                    <p className="photo-label">New photo</p>
                                </div>
                            )}
                            <input
                                type="file"
                                id="profilePhoto"
                                name="profilePhoto"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>

                        {user.gender === 'female' && (
                            <div className="form-group">
                                <label htmlFor="passportPhoto">
                                    Passport Photo
                                    {!canEditPassport && (
                                        <span className="disabled-note"> (Cannot be changed after approval)</span>
                                    )}
                                </label>
                                {currentPassportPhoto && !previews.passportPhoto && (
                                    <div className="current-photo">
                                        <img 
                                            src={`/${currentPassportPhoto}`} 
                                            alt="Current passport" 
                                            className="photo-preview"
                                        />
                                        <p className="photo-label">Current passport</p>
                                    </div>
                                )}
                                {previews.passportPhoto && (
                                    <div className="new-photo">
                                        <img 
                                            src={previews.passportPhoto} 
                                            alt="New passport" 
                                            className="photo-preview"
                                        />
                                        <p className="photo-label">New passport</p>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    id="passportPhoto"
                                    name="passportPhoto"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    disabled={!canEditPassport}
                                />
                                {!canEditPassport && (
                                    <p className="help-text">
                                        Your account has been approved. Passport photo cannot be changed.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="btn-cancel"
                                onClick={() => {
                                    if (user.gender === 'male') {
                                        navigate('/male-dashboard');
                                    } else {
                                        navigate('/female-dashboard');
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn-submit"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditProfile;
